import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { PLUGINS, getErrorMessage } from '../../services/api';
import { Icons } from './DashboardLayout';

// Available plugins (show toggles)
const AVAILABLE_PLUGINS = ['swap', 'transfer', 'wrap', 'buy_tokens', 'bridge'];

// Coming soon plugins (greyed out)
const COMING_SOON_PLUGINS = [
  'burn_tokens', 'lock_vest', 'add_liquidity', 'lock_lp', 'burn_lp',
  'yield_farming', 'precious_metals', 'lending', 'arbitrage', 
  'multi_chain', 'index_fund', 'flash_loans'
];

export default function PluginsPage() {
  const { agents, selectedAgent, setSelectedAgent, token, maxAgents } = useAuth();
  const [pluginStates, setPluginStates] = useState({});
  const [pluginSchemas, setPluginSchemas] = useState({});
  const [loading, setLoading] = useState(true);
  const [configModal, setConfigModal] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(null);

  // Load agent's plugin states
  useEffect(() => {
    if (selectedAgent?.id) {
      loadPluginStates();
    } else {
      setLoading(false);
    }
  }, [selectedAgent?.id]);

  const loadPluginStates = async () => {
    if (!token || !selectedAgent?.id) return;
    setLoading(true);
    try {
      // Always fetch fresh from API to ensure we have latest state
      const res = await api.getAgentPlugins(token, selectedAgent.id);
      if (res.success && res.data?.plugins) {
        const states = {};
        res.data.plugins.forEach(pc => {
          states[pc.plugin] = { enabled: pc.enabled, config: pc.config || {} };
        });
        setPluginStates(states);
      } else if (selectedAgent?.pluginConfigs) {
        // Fallback to context data if API fails
        const states = {};
        selectedAgent.pluginConfigs.forEach(pc => {
          states[pc.plugin] = { enabled: pc.enabled, config: pc.config || {} };
        });
        setPluginStates(states);
      }
    } catch (err) {
      console.error('Failed to load plugin states:', err);
      // Fallback to context data on error
      if (selectedAgent?.pluginConfigs) {
        const states = {};
        selectedAgent.pluginConfigs.forEach(pc => {
          states[pc.plugin] = { enabled: pc.enabled, config: pc.config || {} };
        });
        setPluginStates(states);
      }
    }
    setLoading(false);
  };

  const handleToggle = async (pluginName, enabled) => {
    // Optimistic update
    const previousState = pluginStates[pluginName]?.enabled;
    setPluginStates(prev => ({
      ...prev,
      [pluginName]: { ...prev[pluginName], enabled }
    }));

    try {
      const res = await api.updatePlugin(token, selectedAgent.id, pluginName, { enabled });
      if (!res.success) {
        // Revert on API error
        setPluginStates(prev => ({
          ...prev,
          [pluginName]: { ...prev[pluginName], enabled: previousState }
        }));
        alert(res.error?.message || 'Failed to update plugin');
      }
    } catch (err) {
      // Revert on network error
      setPluginStates(prev => ({
        ...prev,
        [pluginName]: { ...prev[pluginName], enabled: previousState }
      }));
      console.error('Plugin toggle error:', err);
      const errorMsg = getErrorMessage(err);
      alert(errorMsg);
    }
  };

  const handleSaveConfig = async (pluginName, config) => {
    try {
      const res = await api.updatePlugin(token, selectedAgent.id, pluginName, { 
        enabled: pluginStates[pluginName]?.enabled ?? true,
        config 
      });
      if (!res.success) {
        throw new Error(getErrorMessage(res.error));
      }
      setPluginStates(prev => ({
        ...prev,
        [pluginName]: { ...prev[pluginName], config }
      }));
      setConfigModal(null);
    } catch (err) {
      console.error('Failed to save config:', err);
      alert(getErrorMessage(err));
    }
  };

  // Fetch plugin config schema from backend when opening config modal
  const handleOpenConfig = async (plugin, state) => {
    setLoadingSchema(plugin.id);
    try {
      // Try to fetch dynamic schema from backend
      const res = await api.getAgentPlugins(token, selectedAgent.id);
      if (res.success && res.data?.plugins) {
        const pluginData = res.data.plugins.find(p => p.plugin === plugin.id);
        if (pluginData?.configSchema) {
          setPluginSchemas(prev => ({
            ...prev,
            [plugin.id]: pluginData.configSchema
          }));
        }
      }
    } catch (err) {
      console.log('Using default config schema for', plugin.id);
    }
    setLoadingSchema(null);
    setConfigModal({ plugin, state });
  };

  // No agents state
  if (agents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-6">
          <Icons.Plugins size={32} className="text-white/30" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Create an Agent First</h3>
        <p className="text-white/50 text-center max-w-md">
          Plugins are capabilities you grant to your agents. Create an agent to configure its permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with agent selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-white/50 text-sm">
            Control what your agent can do autonomously. Enable plugins to grant capabilities.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedAgent?.id || ''}
            onChange={(e) => {
              const agent = agents.find(a => a.id === e.target.value);
              setSelectedAgent(agent);
            }}
            className="px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-white text-sm outline-none focus:border-white/10 min-w-[180px]"
            data-testid="plugin-agent-selector"
          >
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <span className="text-white/30 text-sm">{agents.length}/{maxAgents} agents</span>
        </div>
      </div>

      {/* Info banner */}
      <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10">
        <p className="text-blue-400/80 text-sm">
          These settings control what <strong>{selectedAgent?.name}</strong> can do when it calls the API. 
          Your agent uses its API key to execute operations autonomously.
        </p>
      </div>

      {/* Available Plugins */}
      <div className="space-y-3">
        <h3 className="text-white font-medium flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-400" />
          Available Capabilities
        </h3>
        
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-[80px] rounded-xl bg-white/[0.02] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {AVAILABLE_PLUGINS.map((pluginId) => {
              const plugin = PLUGINS[pluginId];
              const state = pluginStates[pluginId] || { enabled: false, config: {} };
              
              return (
                <PluginRow
                  key={pluginId}
                  plugin={plugin}
                  enabled={state.enabled}
                  config={state.config}
                  onToggle={(enabled) => handleToggle(pluginId, enabled)}
                  onConfigure={() => handleOpenConfig(plugin, state)}
                  isLoadingSchema={loadingSchema === pluginId}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Coming Soon */}
      <div className="space-y-3">
        <h3 className="text-white/50 font-medium flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-white/20" />
          Coming Soon
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {COMING_SOON_PLUGINS.map((pluginId) => {
            const plugin = PLUGINS[pluginId];
            return (
              <div
                key={pluginId}
                className="p-3 rounded-xl bg-white/[0.01] border border-white/[0.04] opacity-50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/[0.02] flex items-center justify-center">
                      <Icons.Plugins size={14} className="text-white/30" />
                    </div>
                    <div>
                      <p className="text-white/40 text-sm font-medium">{plugin?.name || pluginId}</p>
                      <p className="text-white/20 text-xs">{plugin?.description}</p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[9px] font-medium text-white/30 bg-white/[0.03]">
                    Soon
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Config Modal */}
      <AnimatePresence>
        {configModal && (
          <PluginConfigModal
            plugin={configModal.plugin}
            config={configModal.state.config}
            schema={pluginSchemas[configModal.plugin.id]}
            onSave={(config) => handleSaveConfig(configModal.plugin.id, config)}
            onClose={() => setConfigModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PluginRow({ plugin, enabled, config, onToggle, onConfigure, isLoadingSchema }) {
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await onToggle(!enabled);
    } finally {
      setToggling(false);
    }
  };

  const hasConfig = Object.keys(config || {}).length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl border transition-all ${
        enabled 
          ? 'bg-white/[0.03] border-white/[0.08]' 
          : 'bg-white/[0.01] border-white/[0.04]'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            enabled ? 'bg-green-500/10' : 'bg-white/[0.03]'
          }`}>
            <PluginIcon id={plugin?.id} enabled={enabled} />
          </div>
          <div className="flex-1">
            <p className={`font-medium ${enabled ? 'text-white' : 'text-white/50'}`}>
              {plugin?.name}
            </p>
            <p className="text-white/40 text-sm">{plugin?.description}</p>
            {/* Show config summary if exists */}
            {enabled && hasConfig && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {config.maxSlippage && (
                  <span className="text-xs text-white/30 px-2 py-0.5 rounded bg-white/[0.03]">
                    Slippage: {config.maxSlippage}%
                  </span>
                )}
                {config.maxAmountPerTx && (
                  <span className="text-xs text-white/30 px-2 py-0.5 rounded bg-white/[0.03]">
                    Max: {parseFloat(config.maxAmountPerTx) / 1e18} ETH
                  </span>
                )}
                {config.whitelistedAddresses?.length > 0 && (
                  <span className="text-xs text-white/30 px-2 py-0.5 rounded bg-white/[0.03]">
                    {config.whitelistedAddresses.length} whitelisted
                  </span>
                )}
                {config.allowedChains?.length > 0 && (
                  <span className="text-xs text-white/30 px-2 py-0.5 rounded bg-white/[0.03]">
                    {config.allowedChains.length} chains
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {enabled && (
            <button
              onClick={onConfigure}
              disabled={isLoadingSchema}
              className="text-white/40 text-sm hover:text-white/70 transition-colors flex items-center gap-1 disabled:opacity-50"
              data-testid={`configure-${plugin?.id}-btn`}
            >
              {isLoadingSchema ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-3 h-3 border border-white/40 border-t-white rounded-full"
                />
              ) : (
                'Configure →'
              )}
            </button>
          )}
          <button
            onClick={handleToggle}
            disabled={toggling}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              toggling ? 'opacity-50' : ''
            } ${enabled ? 'bg-green-500' : 'bg-white/10'}`}
            data-testid={`plugin-toggle-${plugin?.id}`}
          >
            {toggling ? (
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <div className="w-3 h-3 border border-white/40 border-t-white rounded-full" />
              </motion.div>
            ) : (
              <motion.div
                className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm"
                animate={{ left: enabled ? 26 : 4 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function PluginIcon({ id, enabled }) {
  const color = enabled ? 'text-green-400' : 'text-white/30';
  
  const icons = {
    swap: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={color}>
        <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/>
      </svg>
    ),
    transfer: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={color}>
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    ),
    wrap: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={color}>
        <circle cx="12" cy="12" r="9"/>
        <path d="M12 8v8m-4-4h8"/>
      </svg>
    ),
    buy_tokens: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={color}>
        <circle cx="12" cy="12" r="9"/>
        <path d="M12 6v12M8 10l4-4 4 4"/>
      </svg>
    ),
    bridge: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={color}>
        <path d="M4 15l4-8 4 8 4-8 4 8M4 19h16"/>
      </svg>
    ),
  };
  
  return icons[id] || <Icons.Plugins size={18} className={color} />;
}

function PluginConfigModal({ plugin, config, schema, onSave, onClose }) {
  const [formConfig, setFormConfig] = useState(config || {});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formConfig);
    } finally {
      setSaving(false);
    }
  };

  // Render dynamic fields from schema if available
  const renderDynamicFields = () => {
    if (!schema?.properties) return null;
    
    return Object.entries(schema.properties).map(([key, fieldSchema]) => {
      const value = formConfig[key];
      const label = fieldSchema.title || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const description = fieldSchema.description;
      
      // Handle different field types
      switch (fieldSchema.type) {
        case 'number':
        case 'integer':
          return (
            <div key={key}>
              <label className="block text-sm text-white/70 mb-2">{label}</label>
              <input
                type="number"
                step={fieldSchema.type === 'integer' ? 1 : 0.01}
                min={fieldSchema.minimum}
                max={fieldSchema.maximum}
                value={value ?? fieldSchema.default ?? ''}
                onChange={(e) => setFormConfig({ ...formConfig, [key]: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none focus:border-white/20"
                data-testid={`config-${key}`}
              />
              {description && <p className="text-white/30 text-xs mt-1">{description}</p>}
            </div>
          );
          
        case 'boolean':
          return (
            <label key={key} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] cursor-pointer">
              <div>
                <span className="text-white/70 text-sm">{label}</span>
                {description && <p className="text-white/30 text-xs mt-0.5">{description}</p>}
              </div>
              <button
                onClick={() => setFormConfig({ ...formConfig, [key]: !value })}
                className={`relative w-10 h-5 rounded-full transition-colors ${value ? 'bg-green-500' : 'bg-white/10'}`}
              >
                <motion.div
                  className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm"
                  animate={{ left: value ? 22 : 2 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              </button>
            </label>
          );
          
        case 'array':
          if (fieldSchema.items?.type === 'string') {
            return (
              <div key={key}>
                <label className="block text-sm text-white/70 mb-2">{label}</label>
                <textarea
                  value={(value || []).join('\n')}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    [key]: e.target.value.split('\n').filter(a => a.trim())
                  })}
                  placeholder="One per line..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none focus:border-white/20"
                  data-testid={`config-${key}`}
                />
                {description && <p className="text-white/30 text-xs mt-1">{description}</p>}
              </div>
            );
          }
          // Enum array (multi-select)
          if (fieldSchema.items?.enum) {
            return (
              <div key={key}>
                <label className="block text-sm text-white/70 mb-2">{label}</label>
                <div className="flex flex-wrap gap-2">
                  {fieldSchema.items.enum.map(option => (
                    <button
                      key={option}
                      onClick={() => {
                        const current = value || [];
                        setFormConfig({
                          ...formConfig,
                          [key]: current.includes(option)
                            ? current.filter(c => c !== option)
                            : [...current, option]
                        });
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                        (value || []).includes(option)
                          ? 'bg-white/10 border-white/20 text-white border'
                          : 'bg-white/[0.03] border-white/[0.06] text-white/50 border'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {description && <p className="text-white/30 text-xs mt-1">{description}</p>}
              </div>
            );
          }
          return null;
          
        case 'string':
          // Enum dropdown
          if (fieldSchema.enum) {
            return (
              <div key={key}>
                <label className="block text-sm text-white/70 mb-2">{label}</label>
                <select
                  value={value ?? fieldSchema.default ?? ''}
                  onChange={(e) => setFormConfig({ ...formConfig, [key]: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                  data-testid={`config-${key}`}
                >
                  <option value="">Select...</option>
                  {fieldSchema.enum.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {description && <p className="text-white/30 text-xs mt-1">{description}</p>}
              </div>
            );
          }
          // Regular text input
          return (
            <div key={key}>
              <label className="block text-sm text-white/70 mb-2">{label}</label>
              <input
                type="text"
                value={value ?? ''}
                onChange={(e) => setFormConfig({ ...formConfig, [key]: e.target.value })}
                placeholder={fieldSchema.default || ''}
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white text-sm outline-none focus:border-white/20"
                data-testid={`config-${key}`}
              />
              {description && <p className="text-white/30 text-xs mt-1">{description}</p>}
            </div>
          );
          
        default:
          return null;
      }
    });
  };

  // Plugin-specific config fields (fallback if no schema)
  const renderConfigFields = () => {
    // If we have a dynamic schema, use it
    if (schema?.properties) {
      const dynamicFields = renderDynamicFields();
      if (dynamicFields && dynamicFields.length > 0) {
        return <>{dynamicFields}</>;
      }
    }
    
    // Fallback to hardcoded fields
    switch (plugin.id) {
      case 'swap':
        return (
          <>
            <div>
              <label className="block text-sm text-white/70 mb-2">Max Slippage (%)</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                max="50"
                value={formConfig.maxSlippage || 1}
                onChange={(e) => setFormConfig({ ...formConfig, maxSlippage: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                data-testid="config-maxSlippage"
              />
              <p className="text-white/30 text-xs mt-1">Maximum price slippage your agent will accept</p>
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Max Amount Per Swap (ETH)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={(parseFloat(formConfig.maxAmountPerTx) || 0) / 1e18 || ''}
                onChange={(e) => setFormConfig({ ...formConfig, maxAmountPerTx: (parseFloat(e.target.value) * 1e18).toString() })}
                placeholder="No limit"
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                data-testid="config-maxAmountPerTx"
              />
              <p className="text-white/30 text-xs mt-1">Maximum ETH value per swap transaction</p>
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Allowed DEX Protocols (optional)</label>
              <div className="flex flex-wrap gap-2">
                {['uniswap', '0x', 'sushiswap', 'curve'].map(dex => (
                  <button
                    key={dex}
                    onClick={() => {
                      const protocols = formConfig.allowedProtocols || [];
                      setFormConfig({
                        ...formConfig,
                        allowedProtocols: protocols.includes(dex)
                          ? protocols.filter(p => p !== dex)
                          : [...protocols, dex]
                      });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                      (formConfig.allowedProtocols || []).includes(dex)
                        ? 'bg-white/10 border-white/20 text-white border'
                        : 'bg-white/[0.03] border-white/[0.06] text-white/50 border'
                    }`}
                  >
                    {dex}
                  </button>
                ))}
              </div>
              <p className="text-white/30 text-xs mt-1">Leave empty to allow all protocols</p>
            </div>
          </>
        );
      
      case 'transfer':
        return (
          <>
            <div>
              <label className="block text-sm text-white/70 mb-2">Max Transfer Amount (ETH)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={(parseFloat(formConfig.maxAmountPerTx) || 0) / 1e18 || ''}
                onChange={(e) => setFormConfig({ ...formConfig, maxAmountPerTx: (parseFloat(e.target.value) * 1e18).toString() })}
                placeholder="No limit"
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                data-testid="config-maxAmountPerTx"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Daily Transfer Limit (ETH)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={(parseFloat(formConfig.dailyLimit) || 0) / 1e18 || ''}
                onChange={(e) => setFormConfig({ ...formConfig, dailyLimit: (parseFloat(e.target.value) * 1e18).toString() })}
                placeholder="No limit"
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                data-testid="config-dailyLimit"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Whitelisted Addresses (optional)</label>
              <textarea
                value={(formConfig.whitelistedAddresses || []).join('\n')}
                onChange={(e) => setFormConfig({ 
                  ...formConfig, 
                  whitelistedAddresses: e.target.value.split('\n').filter(a => a.trim()) 
                })}
                placeholder="0x... (one per line)"
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none"
                data-testid="config-whitelistedAddresses"
              />
              <p className="text-white/30 text-xs mt-1">Leave empty to allow transfers to any address</p>
            </div>
          </>
        );
      
      case 'bridge':
        return (
          <>
            <div>
              <label className="block text-sm text-white/70 mb-2">Max Bridge Amount (ETH)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={(parseFloat(formConfig.maxAmountPerTx) || 0) / 1e18 || ''}
                onChange={(e) => setFormConfig({ ...formConfig, maxAmountPerTx: (parseFloat(e.target.value) * 1e18).toString() })}
                placeholder="No limit"
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                data-testid="config-maxAmountPerTx"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Allowed Destination Chains</label>
              <div className="flex flex-wrap gap-2">
                {['ethereum', 'arbitrum', 'optimism', 'polygon', 'base'].map(chain => (
                  <button
                    key={chain}
                    onClick={() => {
                      const chains = formConfig.allowedChains || [];
                      setFormConfig({
                        ...formConfig,
                        allowedChains: chains.includes(chain) 
                          ? chains.filter(c => c !== chain)
                          : [...chains, chain]
                      });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                      (formConfig.allowedChains || []).includes(chain)
                        ? 'bg-white/10 border-white/20 text-white border'
                        : 'bg-white/[0.03] border-white/[0.06] text-white/50 border'
                    }`}
                  >
                    {chain}
                  </button>
                ))}
              </div>
              <p className="text-white/30 text-xs mt-1">Select which chains your agent can bridge to</p>
            </div>
          </>
        );
        
      case 'wrap':
        return (
          <>
            <div>
              <label className="block text-sm text-white/70 mb-2">Max Wrap/Unwrap Amount (ETH)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={(parseFloat(formConfig.maxAmountPerTx) || 0) / 1e18 || ''}
                onChange={(e) => setFormConfig({ ...formConfig, maxAmountPerTx: (parseFloat(e.target.value) * 1e18).toString() })}
                placeholder="No limit"
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                data-testid="config-maxAmountPerTx"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Allowed Actions</label>
              <div className="flex gap-2">
                {['wrap', 'unwrap'].map(action => (
                  <button
                    key={action}
                    onClick={() => {
                      const actions = formConfig.allowedActions || ['wrap', 'unwrap'];
                      setFormConfig({
                        ...formConfig,
                        allowedActions: actions.includes(action)
                          ? actions.filter(a => a !== action)
                          : [...actions, action]
                      });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm capitalize ${
                      (formConfig.allowedActions || ['wrap', 'unwrap']).includes(action)
                        ? 'bg-white/10 border-white/20 text-white border'
                        : 'bg-white/[0.03] border-white/[0.06] text-white/50 border'
                    }`}
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          </>
        );
        
      case 'buy_tokens':
        return (
          <>
            <div>
              <label className="block text-sm text-white/70 mb-2">Max Purchase Amount (ETH)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={(parseFloat(formConfig.maxAmountPerTx) || 0) / 1e18 || ''}
                onChange={(e) => setFormConfig({ ...formConfig, maxAmountPerTx: (parseFloat(e.target.value) * 1e18).toString() })}
                placeholder="No limit"
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
                data-testid="config-maxAmountPerTx"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Allowed Tokens (optional)</label>
              <textarea
                value={(formConfig.allowedTokens || []).join('\n')}
                onChange={(e) => setFormConfig({ 
                  ...formConfig, 
                  allowedTokens: e.target.value.split('\n').filter(a => a.trim()) 
                })}
                placeholder="Token address or symbol (one per line)"
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none"
                data-testid="config-allowedTokens"
              />
              <p className="text-white/30 text-xs mt-1">Leave empty to allow purchasing any token</p>
            </div>
          </>
        );
      
      default:
        return (
          <p className="text-white/40 text-sm text-center py-4">
            No additional configuration available for this plugin.
          </p>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
              <PluginIcon id={plugin.id} enabled={true} />
            </div>
            <h2 className="text-lg font-semibold text-white">Configure {plugin.name}</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {renderConfigFields()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-white/60 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <motion.button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
            whileTap={{ scale: 0.98 }}
            data-testid="save-plugin-config-btn"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
